import { Product } from '@@types'
import { URL_PAGE_PRODUCT } from '@hive/platform-url-schema'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'


import { useCartActions, useCartStore } from '@entities/Cart'
import { calculateItemTotal } from '@entities/Product/helpers'

import { useAuth } from '@features/auth'
import ControlCart from '@features/cart/ui/control/CartControl'
import CartDeleteProductButton from '@features/cart/ui/delete-product/CartDeleteProductButton'
import { useErrorInfoModal } from '@features/modal'

import useProductsSimilarModal from '@widgets/products-similar/hooks/useProductsSimilarModal'



import { cn } from '@shared/lib/cn'
import { LocalizedLink as Link } from '@shared/lib/locale'
import ProductPlaceholder from '@shared/ui/product-placeholder'
import { formatPrice } from '@shared/uttils'

import styles from './styles.module.scss'




interface CartListPreviewProps {
  data: Product[];
}

export default function CartListPreview (props: CartListPreviewProps) {
  const { t: te } = useTranslation('errors')
  const { t: tc } = useTranslation('cart')
  const { data } = props
  const { getProduct, pendingRemovals, removingProducts } = useCartStore()
  const { isAuthorized } = useAuth()
  const { openProductsSimilarModal } = useProductsSimilarModal()
  const { removeProductImmediately, cancelPendingRemoval } = useCartActions()
  const { openErrorInfoModal } = useErrorInfoModal()

  const handleRemove = async (productId: string) => {
    try {
      await removeProductImmediately(productId, isAuthorized)
    }
    catch (error) {
      console.error('Error removing product:', error)
      openErrorInfoModal({
        description: te('drop_cart_item'),
      })
    }
  }

  return (
    <div className={styles.list}>
      {data.map((item) => {
        const productId = String(item.id)
        const cartItem = getProduct(productId)
        const isPendingRemoval = pendingRemovals.has(productId)
        const isRemoving = removingProducts.has(productId)
        // OUT_STOCK проверяем только если товар не в процессе удаления
        const OUT_STOCK = !isPendingRemoval && !isRemoving && (!cartItem?.amount || cartItem?.isSoldOut)

        const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
          if (OUT_STOCK) {
            e.preventDefault()
            openProductsSimilarModal(item)
          }
        }

        const handleRestore = () => {
          cancelPendingRemoval(productId)
        }

        // Если товар в процессе удаления (pending removal)
        if (isPendingRemoval) {
          return (
            <div
              className={cn(styles['preview-cart-item'], styles['pending-removal'])}
              key={item.id}
            >
              <Link
                className={styles['preview-cart-item__image']}
                to={generatePath(URL_PAGE_PRODUCT, { slug: item.slug })}
              >
                <ProductPlaceholder
                  src={item.preview?.url}
                  alt=""
                />
              </Link>

              <div className={styles['preview-cart-item__content']}>
                <Link
                  className={styles['preview-cart-item__title']}
                  to={generatePath(URL_PAGE_PRODUCT, { slug: item.slug })}
                >
                  {item.name}
                </Link>

                <div className={styles['preview-cart-item__bottom']}>
                  <div className={styles['preview-cart-item__pending-actions']}>
                    <button
                      className={styles['preview-cart-item__restore-btn']}
                      onClick={handleRestore}
                    >
                      {tc('restore')}
                    </button>
                    <span className={styles['preview-cart-item__removing-text']}>
                      {tc('removing')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        // Если товар удаляется на сервере (после истечения debounce)
        if (isRemoving) {
          return (
            <div
              className={cn(styles['preview-cart-item'], styles['removing'])}
              key={item.id}
            >
              <Link
                className={styles['preview-cart-item__image']}
                to={generatePath(URL_PAGE_PRODUCT, { slug: item.slug })}
              >
                <ProductPlaceholder
                  src={item.preview?.url}
                  alt=""
                />
              </Link>

              <div className={styles['preview-cart-item__content']}>
                <Link
                  className={styles['preview-cart-item__title']}
                  to={generatePath(URL_PAGE_PRODUCT, { slug: item.slug })}
                >
                  {item.name}
                </Link>

                <div className={styles['preview-cart-item__bottom']}>
                  <div className={styles['preview-cart-item__removing-loader']}>
                    <span className={styles['preview-cart-item__spinner']} />
                    <span className={styles['preview-cart-item__removing-text']}>
                      {tc('deleting')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        return (
          <div
            className={cn(styles['preview-cart-item'], {
              [styles.out]: OUT_STOCK,
            })}
            key={item.id}
          >
            <Link
              className={styles['preview-cart-item__image']}
              onClick={handleClick}
              to={generatePath(URL_PAGE_PRODUCT, { slug: item.slug })}
            >
              <ProductPlaceholder
                src={item.preview?.url}
                alt=""
              />
            </Link>

            <div className={styles['preview-cart-item__content']}>
              <Link
                className={styles['preview-cart-item__title']}
                to={generatePath(URL_PAGE_PRODUCT, { slug: item.slug })}
                onClick={handleClick}
              >
                {item.name}
              </Link>

              <div className={styles['preview-cart-item__bottom']}>
                {!OUT_STOCK && (
                  <>
                    <ControlCart isTablet={true} size="small" product={item} />

                    <div className={styles['preview-cart-item__price']}>
                      {item.priceOffer?.price && (
                        <span
                          className={cn(
                            styles['preview-cart-item__price-value'],
                          )}
                        >
                          {formatPrice(calculateItemTotal(item.priceOffer.price, cartItem?.amount, item.priceOffer?.count_multiplier))}
                        </span>
                      )}

                      {!!item.priceOffer?.oldPrice && (
                        <span
                          className={styles['preview-cart-item__price-old']}
                        >
                          {formatPrice(calculateItemTotal(item.priceOffer?.oldPrice, cartItem?.amount, item.priceOffer?.count_multiplier))}
                        </span>
                      )}
                    </div>
                  </>
                )}

                {OUT_STOCK && (
                  <CartDeleteProductButton
                    className={styles['preview-cart-item__delete']}
                    onClick={() => handleRemove(productId)}
                  />
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
